import { memo, useCallback } from 'react';
import { Layers } from 'react-feather';
import type { WidgetInstance } from '@trinity/chat';
import { DashboardWidgetTypeEnum, type WidgetSpecificConfig } from '@trinity/chat/types/dashboard-config';
import { useDashboardBuilderEditing } from '@trinity/chat-components/containers/Dashboard/context';
import { AddWidgetPage } from './AddWidgetPage/AddWidgetPage.js';
import { useBuilderPalette, type PaletteEntry } from './PaletteContext.js';
import { WIDGET_BUILDER_INFO } from './widget-builder-info.js';

type Props = {
  open: boolean;
  onClose: () => void;
  /**
   * If provided, called with the selected type instead of auto-adding a block. `config` carries what
   * the pick implies beyond the type (a chart template starts as its chart type).
   */
  onSelect?: (type: string, config?: Partial<WidgetSpecificConfig>) => void;
  /** Called with the picked project widget when a saved widget is chosen */
  onSelectLibraryWidget?: (widget: WidgetInstance) => void;
  /** When true, the Section card is hidden (used inside sections to prevent nesting) */
  hideSections?: boolean;
};

// The management builder's entries come from WIDGET_BUILDER_INFO (one per widget type, in enum order),
// followed by the section entry. Library-backed builders pass their own entries via their profile.
const PALETTE_ENTRIES: PaletteEntry[] = [
  ...DashboardWidgetTypeEnum.options.flatMap((type): PaletteEntry[] => {
    const tile = WIDGET_BUILDER_INFO[type].palette;
    return tile ? [{ type, label: tile.label, icon: tile.icon }] : [];
  }),
  { type: 'section', label: 'Section', icon: Layers },
];

/**
 * WidgetPalette — the widget chooser every builder opens. It hands the builder's entries and library
 * to the shared Add Widget page: builders with a palette provider (segments, patient journey, media
 * library) pick from their project's saved widgets, the management builder picks a type to add data to.
 *
 * Plain picks call `onSelect(type, config?)`; picking a saved widget calls `onSelectLibraryWidget(widget)`.
 */
export const WidgetPalette = memo(function WidgetPalette({
  open,
  onClose,
  onSelect,
  onSelectLibraryWidget,
  hideSections = false,
}: Props): React.ReactElement {
  const { addBlockWithWidget } = useDashboardBuilderEditing();
  const palette = useBuilderPalette();

  const handleSelectType = useCallback(
    (type: string, config?: Partial<WidgetSpecificConfig>) => {
      if (onSelect) {
        onSelect(type, config);
      } else {
        addBlockWithWidget(type, config ?? {});
      }
    },
    [onSelect, addBlockWithWidget],
  );

  const handleSelectLibraryWidget = useCallback(
    (widget: WidgetInstance) => onSelectLibraryWidget?.(widget),
    [onSelectLibraryWidget],
  );

  const mode = palette ? 'library' : 'new';
  const entries = palette?.entries ?? PALETTE_ENTRIES;
  // Builders offering the same widgets share a Recent row; one offering different ones gets its own.
  const recentScope = `${mode}:${entries.map((e) => e.library ?? e.type).join(',')}`;

  return (
    <AddWidgetPage
      open={open}
      onClose={onClose}
      mode={mode}
      entries={entries}
      recentScope={recentScope}
      hideSections={hideSections}
      libraryWidgets={palette?.libraryWidgets}
      widgetComponentMap={palette?.widgetComponentMap}
      onRefetchLibrary={palette?.refetchLibraryWidgets}
      onSelectType={handleSelectType}
      onSelectLibraryWidget={handleSelectLibraryWidget}
    />
  );
});
